/**
 * How a situation-aware recommendation explains itself. Every promotion and
 * every discouragement renders its text next to the tool it concerns —
 * verbatim, never paraphrased, never hidden. A recommendation the user cannot
 * interrogate is worse than none.
 */

import { AlertTriangle, ArrowUpRight, Sparkles } from 'lucide-react';
import { Callout } from '../../ds';
import { annotationsForTool, type SituationRecommendation } from '../../../lib/situationRecommendation';
import { DECISION_CLASS_LABELS } from '../../../types/prioritization/session';

/** Callout shown above a tool grid when a fired rule says: run a different session first. */
export function SituationEscapeNotice({ recommendation }: { recommendation: SituationRecommendation | null }) {
  const escape = recommendation?.escape;
  if (!escape) return null;
  return (
    <Callout tone="mustard" label="The room" title={`Run ${DECISION_CLASS_LABELS[escape.toDecisionClass]} first`} className="mb-4">
      <p className="text-sm">{escape.because}</p>
      <p className="mt-1 text-[11px] text-[var(--text-ter)]">Rule: {escape.ruleId}. You can still pick any method below.</p>
    </Callout>
  );
}

/** One-line basis note for a grid whose order was adjusted by the situation rules. */
export function SituationBasisNote({ recommendation }: { recommendation: SituationRecommendation | null }) {
  if (!recommendation?.applied) return null;
  const ids = recommendation.firedRules.map((r) => r.id).join(', ');
  return (
    <p className="mb-3 flex items-start gap-1.5 text-xs text-[var(--text-sec)]" data-testid="situation-basis">
      <Sparkles size={13} className="mt-0.5 shrink-0 text-[var(--text-ter)]" />
      <span>
        Order adjusted for the room you declared. Reasons are shown on each method; the rules that fired: {ids}.
      </span>
    </p>
  );
}

/**
 * Per-tool annotation block. `toolId` is in the shape-matrix id space
 * ('dot-voting'); callers bridge from registry ids with situationToolIds.
 */
export function SituationToolNote({
  recommendation,
  toolId,
}: {
  recommendation: SituationRecommendation | null;
  toolId: string | undefined;
}) {
  if (!recommendation?.applied || !toolId) return null;
  const notes = annotationsForTool(recommendation, toolId);
  if (notes.length === 0) return null;
  return (
    <div className="mt-2 space-y-1.5" data-testid={`situation-note-${toolId}`}>
      {notes.map((note, i) => (
        <div
          key={`${note.ruleId}-${note.kind}-${i}`}
          className={[
            'border-l-2 pl-2 text-[11px] leading-snug',
            note.kind === 'promoted'
              ? 'border-[var(--green)] text-[var(--text-sec)]'
              : 'border-[var(--mustard)] text-[var(--text-sec)]',
          ].join(' ')}
        >
          <span
            className={[
              'mr-1 inline-flex items-center gap-0.5 font-semibold uppercase tracking-wide',
              note.kind === 'promoted' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]',
            ].join(' ')}
          >
            {note.kind === 'promoted' ? <ArrowUpRight size={10} /> : <AlertTriangle size={10} />}
            {note.kind === 'promoted' ? 'Fits this room' : 'Consider'}
          </span>
          {note.text}
        </div>
      ))}
    </div>
  );
}
